import { useState, useRef, useEffect, useCallback } from 'react';
import { router, Link } from '@inertiajs/react';
import GmLayout from '@/layouts/GmLayout';
import { GmItemsFormProps, ItemRarityValue, CraftingMaterial, LearnableBy } from '@/types/items';

/**
 * Auto-tag prefixes to identify auto-generated tags.
 * These help distinguish between manually added and auto-added tags.
 */
const AUTO_TAG_CATEGORIES = {
    rarity: 'rarity:',
    stat: 'stat:',
    skill: 'skill:',
    building: 'building:',
    learnable: 'learnable',
} as const;

/**
 * Material types for crafting.
 */
const MATERIAL_TYPES = [
    { value: 'tea', label: 'Tea' },
    { value: 'wood', label: 'Wood' },
    { value: 'mystical', label: 'Mystical' },
    { value: 'medicinal', label: 'Medicinal' },
    { value: 'edible', label: 'Edible' },
] as const;

/**
 * Allowed image MIME types for upload.
 */
const ALLOWED_IMAGE_TYPES = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];

/**
 * Maximum image file size in bytes (10MB).
 */
const MAX_IMAGE_SIZE = 10 * 1024 * 1024;

/**
 * GM Items Edit page - form to edit an existing item recipe.
 */
export default function Edit({ recipe, rarities, stats, allTags, buildingTypes }: GmItemsFormProps) {
    // Recipe must exist for edit page
    if (!recipe) {
        return (
            <GmLayout title="Edit Item Recipe" activePath="/gm/items">
                <div className="text-center py-12 text-gray-400">
                    Recipe not found.
                </div>
            </GmLayout>
        );
    }

    const [name, setName] = useState(recipe.name);
    const [description, setDescription] = useState(recipe.description || '');
    const [rarity, setRarity] = useState<ItemRarityValue>(recipe.rarity.value);
    const [customStatCount, setCustomStatCount] = useState<number | ''>(recipe.stat_count || '');
    const [customStatMin, setCustomStatMin] = useState<number | ''>(recipe.base_stat_range?.[0] || '');
    const [customStatMax, setCustomStatMax] = useState<number | ''>(recipe.base_stat_range?.[1] || '');
    const [allowedStats, setAllowedStats] = useState<string[]>(recipe.allowed_stats || []);
    const [fixedStats, setFixedStats] = useState<Record<string, number>>(recipe.fixed_stats || {});
    const [useFixedStats, setUseFixedStats] = useState(!!recipe.fixed_stats);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [errors, setErrors] = useState<Record<string, string>>({});

    // Image upload state
    const [imageFile, setImageFile] = useState<File | null>(null);
    const [imagePreview, setImagePreview] = useState<string | null>(null);
    const [removeImage, setRemoveImage] = useState(false);
    const fileInputRef = useRef<HTMLInputElement>(null);

    // Current image from recipe (medium size for display)
    const currentImage = recipe.image?.medium || null;

    // Tag state - initialize with recipe's existing tags
    const [selectedTags, setSelectedTags] = useState<string[]>(
        recipe.tags?.map(tag => tag.name) || []
    );
    const [newTagInput, setNewTagInput] = useState('');

    // Crafting requirements state - initialize with recipe's existing data
    const [requiredBuildingTypeId, setRequiredBuildingTypeId] = useState<number | ''>(
        recipe.required_building_type_id || ''
    );
    const [requiredBuildingLevel, setRequiredBuildingLevel] = useState<number>(
        recipe.required_building_level || 1
    );
    const [requiredBuildingStars, setRequiredBuildingStars] = useState<number>(
        recipe.required_building_stars || 1
    );
    const [craftingMaterials, setCraftingMaterials] = useState<CraftingMaterial[]>(
        recipe.crafting_materials || []
    );
    const [learnableBy, setLearnableBy] = useState<LearnableBy>(recipe.learnable_by || 'no');

    // Track which tags were auto-added (vs manually added)
    // This allows us to only auto-remove tags that were auto-added
    const [autoTags, setAutoTags] = useState<Set<string>>(new Set());

    // Get the selected rarity info
    const selectedRarity = rarities.find(r => r.value === rarity);
    const isUnique = rarity === 'unique';

    // Get the selected building type name
    const selectedBuildingType = buildingTypes.find(bt => bt.id === requiredBuildingTypeId);

    /**
     * Compute what auto-tags should exist based on current form state.
     * Returns a Set of tag names that should be auto-added.
     */
    const computeExpectedAutoTags = useCallback((): Set<string> => {
        const tags = new Set<string>();

        // Rarity tag
        if (rarity) {
            tags.add(`${AUTO_TAG_CATEGORIES.rarity}${rarity}`);
        }

        // Stat tags (from allowed stats or fixed stats)
        const activeStats = useFixedStats
            ? Object.keys(fixedStats).filter(k => fixedStats[k] > 0)
            : allowedStats;

        activeStats.forEach(stat => {
            // Check if it's a stat or skill based on the stats prop structure
            if (stats.stats[stat]) {
                tags.add(`${AUTO_TAG_CATEGORIES.stat}${stat}`);
            } else if (stats.skills[stat]) {
                tags.add(`${AUTO_TAG_CATEGORIES.skill}${stat}`);
            }
        });

        // Building tag
        if (requiredBuildingTypeId && selectedBuildingType) {
            tags.add(`${AUTO_TAG_CATEGORIES.building}${selectedBuildingType.name.toLowerCase()}`);
        }

        // Learnable tag
        if (learnableBy !== 'no') {
            tags.add(AUTO_TAG_CATEGORIES.learnable);
        }

        return tags;
    }, [rarity, allowedStats, fixedStats, useFixedStats, stats, requiredBuildingTypeId, selectedBuildingType, learnableBy]);

    /**
     * Sync auto-tags when properties change.
     * Adds new auto-tags and removes obsolete auto-tags.
     */
    useEffect(() => {
        const expectedAutoTags = computeExpectedAutoTags();

        setSelectedTags(prevTags => {
            const newTags = new Set(prevTags);

            // Add new auto-tags that aren't already selected
            expectedAutoTags.forEach(tag => {
                newTags.add(tag);
            });

            // Remove old auto-tags that are no longer needed
            autoTags.forEach(tag => {
                if (!expectedAutoTags.has(tag)) {
                    newTags.delete(tag);
                }
            });

            return Array.from(newTags);
        });

        // Update the set of auto-tags
        setAutoTags(expectedAutoTags);
    }, [computeExpectedAutoTags]);

    /**
     * Handle image file selection.
     * Validates file type and size, then creates a preview.
     */
    const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (!file) {
            setImageFile(null);
            setImagePreview(null);
            return;
        }

        // Validate file type
        if (!ALLOWED_IMAGE_TYPES.includes(file.type)) {
            setErrors(prev => ({ ...prev, image: 'Please select a valid image file (JPEG, PNG, GIF, or WebP)' }));
            return;
        }

        // Validate file size
        if (file.size > MAX_IMAGE_SIZE) {
            setErrors(prev => ({ ...prev, image: 'Image must be less than 10MB' }));
            return;
        }

        // Clear any previous image error and remove flag
        setErrors(prev => {
            const { image, ...rest } = prev;
            return rest;
        });
        setRemoveImage(false);

        // Set file and create preview
        setImageFile(file);
        const reader = new FileReader();
        reader.onloadend = () => {
            setImagePreview(reader.result as string);
        };
        reader.readAsDataURL(file);
    };

    /**
     * Remove the selected/current image.
     */
    const handleRemoveImage = () => {
        setImageFile(null);
        setImagePreview(null);
        setRemoveImage(true);
        if (fileInputRef.current) {
            fileInputRef.current.value = '';
        }
    };

    /**
     * Handle form submission.
     * Uses FormData to support file upload.
     */
    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        setIsSubmitting(true);
        setErrors({});

        // Build form data object for Inertia
        const formData: Record<string, unknown> = {
            name,
            description: description || null,
            rarity,
            custom_stat_count: isUnique && customStatCount ? customStatCount : null,
            custom_stat_min: isUnique && customStatMin ? customStatMin : null,
            custom_stat_max: isUnique && customStatMax ? customStatMax : null,
            allowed_stats: allowedStats.length > 0 ? allowedStats : null,
            fixed_stats: useFixedStats && Object.keys(fixedStats).length > 0 ? fixedStats : null,
            required_building_type_id: requiredBuildingTypeId || null,
            required_building_level: requiredBuildingTypeId ? requiredBuildingLevel : null,
            required_building_stars: requiredBuildingTypeId ? requiredBuildingStars : null,
            learnable_by: learnableBy,
            crafting_materials: craftingMaterials.length > 0 ? craftingMaterials : null,
            tags: selectedTags,
        };

        // Add image if selected
        if (imageFile) {
            formData.image = imageFile;
        }

        // Add remove_image flag if removing existing image
        if (removeImage && !imageFile) {
            formData.remove_image = true;
        }

        // Use POST with _method override for file uploads with PATCH
        router.post(`/gm/items/${recipe.id}`, {
            ...formData,
            _method: 'PATCH',
        }, {
            forceFormData: true,
            onError: (errs) => {
                setErrors(errs);
                setIsSubmitting(false);
            },
            onSuccess: () => setIsSubmitting(false),
        });
    };

    /**
     * Toggle a tag in selected tags.
     */
    const toggleTag = (tagName: string) => {
        setSelectedTags(prev =>
            prev.includes(tagName)
                ? prev.filter(t => t !== tagName)
                : [...prev, tagName]
        );
    };

    /**
     * Add a new tag from the input field.
     */
    const handleAddNewTag = () => {
        const trimmed = newTagInput.trim().toLowerCase();
        if (trimmed && !selectedTags.includes(trimmed)) {
            setSelectedTags(prev => [...prev, trimmed]);
            setNewTagInput('');
        }
    };

    /**
     * Handle Enter key in new tag input.
     */
    const handleNewTagKeyDown = (e: React.KeyboardEvent) => {
        if (e.key === 'Enter') {
            e.preventDefault();
            handleAddNewTag();
        }
    };

    /**
     * Toggle a stat in allowed stats.
     */
    const toggleAllowedStat = (stat: string) => {
        setAllowedStats(prev =>
            prev.includes(stat)
                ? prev.filter(s => s !== stat)
                : [...prev, stat]
        );
    };

    /**
     * Update a fixed stat value.
     */
    const updateFixedStat = (stat: string, value: number | '') => {
        if (value === '' || value === 0) {
            const newStats = { ...fixedStats };
            delete newStats[stat];
            setFixedStats(newStats);
        } else {
            setFixedStats(prev => ({ ...prev, [stat]: value }));
        }
    };

    /**
     * Add a new crafting material.
     */
    const addCraftingMaterial = () => {
        // Find a material type not already in use
        const usedTypes = craftingMaterials.map(m => m.type);
        const availableType = MATERIAL_TYPES.find(m => !usedTypes.includes(m.value));
        if (availableType) {
            setCraftingMaterials([...craftingMaterials, { type: availableType.value, quantity: 50 }]);
        }
    };

    /**
     * Remove a crafting material.
     */
    const removeCraftingMaterial = (index: number) => {
        setCraftingMaterials(craftingMaterials.filter((_, i) => i !== index));
    };

    /**
     * Update a crafting material.
     */
    const updateCraftingMaterial = (index: number, field: keyof CraftingMaterial, value: string | number) => {
        const updated = [...craftingMaterials];
        if (field === 'type') {
            updated[index] = { ...updated[index], type: value as CraftingMaterial['type'] };
        } else {
            updated[index] = { ...updated[index], quantity: value as number };
        }
        setCraftingMaterials(updated);
    };

    // Determine which image to show: new preview, current image, or none
    const displayImage = imagePreview || (!removeImage && currentImage) || null;

    return (
        <GmLayout title={`Edit: ${recipe.name}`} activePath="/gm/items">
            <div className="max-w-2xl">
                <div className="flex items-center justify-between mb-6">
                    <h1 className="text-2xl font-bold text-white">Edit Item Recipe</h1>
                    <Link
                        href="/gm/items"
                        className="text-gray-400 hover:text-white transition-colors"
                    >
                        ← Back to Recipes
                    </Link>
                </div>

                <form onSubmit={handleSubmit} className="space-y-6">
                    {/* Name */}
                    <div>
                        <label className="block text-sm font-medium text-gray-300 mb-1">
                            Name <span className="text-red-400">*</span>
                        </label>
                        <input
                            type="text"
                            value={name}
                            onChange={(e) => setName(e.target.value)}
                            className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded text-white placeholder-gray-400"
                            placeholder="Sword of Destiny"
                            required
                        />
                        {errors.name && <p className="mt-1 text-sm text-red-400">{errors.name}</p>}
                    </div>

                    {/* Description */}
                    <div>
                        <label className="block text-sm font-medium text-gray-300 mb-1">
                            Description
                        </label>
                        <textarea
                            value={description}
                            onChange={(e) => setDescription(e.target.value)}
                            rows={3}
                            className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded text-white placeholder-gray-400"
                            placeholder="A legendary blade forged in the fires of Mount Doom..."
                        />
                        {errors.description && <p className="mt-1 text-sm text-red-400">{errors.description}</p>}
                    </div>

                    {/* Image Upload */}
                    <div>
                        <label className="block text-sm font-medium text-gray-300 mb-1">
                            Item Image
                        </label>
                        <div className="flex items-start gap-4">
                            {/* Image Preview (new or existing) */}
                            {displayImage && (
                                <div className="relative">
                                    <img
                                        src={displayImage}
                                        alt="Preview"
                                        className="w-32 h-32 object-cover rounded-lg border border-slate-600"
                                    />
                                    <button
                                        type="button"
                                        onClick={handleRemoveImage}
                                        className="absolute -top-2 -right-2 w-6 h-6 bg-red-600 hover:bg-red-700 text-white rounded-full flex items-center justify-center text-sm"
                                        title="Remove image"
                                    >
                                        ×
                                    </button>
                                    {/* Show badge if this is the current saved image */}
                                    {!imagePreview && currentImage && !removeImage && (
                                        <span className="absolute -bottom-2 left-1/2 -translate-x-1/2 text-xs bg-slate-700 text-gray-300 px-2 py-0.5 rounded">
                                            Current
                                        </span>
                                    )}
                                </div>
                            )}

                            {/* Upload Area */}
                            <div className="flex-1">
                                <input
                                    ref={fileInputRef}
                                    type="file"
                                    accept="image/jpeg,image/png,image/gif,image/webp"
                                    onChange={handleImageChange}
                                    className="hidden"
                                    id="image-upload"
                                />
                                <label
                                    htmlFor="image-upload"
                                    className="flex flex-col items-center justify-center w-full h-32 border-2 border-dashed border-slate-600 rounded-lg cursor-pointer hover:border-purple-500 hover:bg-slate-800/50 transition-colors"
                                >
                                    <svg className="w-8 h-8 text-gray-400 mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                                    </svg>
                                    <span className="text-sm text-gray-400">
                                        {displayImage ? 'Click to change image' : 'Click to upload image'}
                                    </span>
                                    <span className="text-xs text-gray-500 mt-1">
                                        JPEG, PNG, GIF, WebP (max 10MB)
                                    </span>
                                </label>
                            </div>
                        </div>
                        {errors.image && <p className="mt-1 text-sm text-red-400">{errors.image}</p>}
                    </div>

                    {/* Rarity */}
                    <div>
                        <label className="block text-sm font-medium text-gray-300 mb-1">
                            Rarity <span className="text-red-400">*</span>
                        </label>
                        <select
                            value={rarity}
                            onChange={(e) => setRarity(e.target.value as ItemRarityValue)}
                            className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded text-white"
                        >
                            {rarities.map((r) => (
                                <option key={r.value} value={r.value}>
                                    {r.label} - {r.statCount ?? 'Custom'} stat(s), {r.baseStatRange ? `${r.baseStatRange[0]}-${r.baseStatRange[1]}` : 'Custom range'}
                                </option>
                            ))}
                        </select>
                        {errors.rarity && <p className="mt-1 text-sm text-red-400">{errors.rarity}</p>}
                        {selectedRarity && (
                            <p className={`mt-1 text-sm ${selectedRarity.tailwindClass}`}>
                                {selectedRarity.label}: {selectedRarity.statCount ?? 'Custom'} stat(s), range {selectedRarity.baseStatRange ? `${selectedRarity.baseStatRange[0]}-${selectedRarity.baseStatRange[1]}` : 'Custom'}
                            </p>
                        )}
                    </div>

                    {/* Unique-only custom stats */}
                    {isUnique && (
                        <div className="bg-slate-800 border border-cyan-600 rounded-lg p-4 space-y-4">
                            <h3 className="text-cyan-400 font-medium">Unique Item Settings</h3>
                            <div className="grid grid-cols-3 gap-4">
                                <div>
                                    <label className="block text-sm text-gray-400 mb-1">Stat Count</label>
                                    <input
                                        type="number"
                                        value={customStatCount}
                                        onChange={(e) => setCustomStatCount(e.target.value ? parseInt(e.target.value) : '')}
                                        min={1}
                                        max={6}
                                        className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded text-white"
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm text-gray-400 mb-1">Min Value</label>
                                    <input
                                        type="number"
                                        value={customStatMin}
                                        onChange={(e) => setCustomStatMin(e.target.value ? parseInt(e.target.value) : '')}
                                        min={1}
                                        max={100}
                                        className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded text-white"
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm text-gray-400 mb-1">Max Value</label>
                                    <input
                                        type="number"
                                        value={customStatMax}
                                        onChange={(e) => setCustomStatMax(e.target.value ? parseInt(e.target.value) : '')}
                                        min={1}
                                        max={100}
                                        className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded text-white"
                                    />
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Allowed Stats/Skills */}
                    <div>
                        <label className="block text-sm font-medium text-gray-300 mb-2">
                            Allowed Bonuses <span className="text-gray-500">(optional - leave empty for all stats &amp; skills)</span>
                        </label>

                        {/* Stats Section */}
                        <div className="mb-3">
                            <h4 className="text-xs font-medium text-gray-400 mb-2 uppercase tracking-wide">Stats</h4>
                            <div className="grid grid-cols-3 gap-2">
                                {Object.entries(stats.stats).map(([key, label]) => (
                                    <label key={key} className="flex items-center gap-2 text-sm text-gray-300">
                                        <input
                                            type="checkbox"
                                            checked={allowedStats.includes(key)}
                                            onChange={() => toggleAllowedStat(key)}
                                            className="rounded bg-slate-700 border-slate-600 text-purple-600"
                                        />
                                        {label}
                                    </label>
                                ))}
                            </div>
                        </div>

                        {/* Skills Section */}
                        <div>
                            <h4 className="text-xs font-medium text-gray-400 mb-2 uppercase tracking-wide">Skills</h4>
                            <div className="grid grid-cols-3 gap-2">
                                {Object.entries(stats.skills).map(([key, label]) => (
                                    <label key={key} className="flex items-center gap-2 text-sm text-gray-300">
                                        <input
                                            type="checkbox"
                                            checked={allowedStats.includes(key)}
                                            onChange={() => toggleAllowedStat(key)}
                                            className="rounded bg-slate-700 border-slate-600 text-purple-600"
                                        />
                                        {label}
                                    </label>
                                ))}
                            </div>
                        </div>
                    </div>

                    {/* Fixed Stats/Skills */}
                    <div>
                        <label className="flex items-center gap-2 text-sm font-medium text-gray-300 mb-2">
                            <input
                                type="checkbox"
                                checked={useFixedStats}
                                onChange={(e) => setUseFixedStats(e.target.checked)}
                                className="rounded bg-slate-700 border-slate-600 text-purple-600"
                            />
                            Use Fixed Bonuses <span className="text-gray-500">(override random rolling)</span>
                        </label>
                        {useFixedStats && (
                            <div className="mt-3 p-4 bg-slate-800 rounded-lg space-y-4">
                                {/* Fixed Stats */}
                                <div>
                                    <h4 className="text-xs font-medium text-gray-400 mb-2 uppercase tracking-wide">Stats</h4>
                                    <div className="grid grid-cols-2 gap-3">
                                        {Object.entries(stats.stats).map(([key, label]) => (
                                            <div key={key} className="flex items-center gap-2">
                                                <label className="text-sm text-gray-400 w-32">{label}</label>
                                                <input
                                                    type="number"
                                                    value={fixedStats[key] || ''}
                                                    onChange={(e) => updateFixedStat(key, e.target.value ? parseInt(e.target.value) : '')}
                                                    min={1}
                                                    max={100}
                                                    placeholder="0"
                                                    className="w-20 px-2 py-1 bg-slate-700 border border-slate-600 rounded text-white text-sm"
                                                />
                                            </div>
                                        ))}
                                    </div>
                                </div>

                                {/* Fixed Skills */}
                                <div>
                                    <h4 className="text-xs font-medium text-gray-400 mb-2 uppercase tracking-wide">Skills</h4>
                                    <div className="grid grid-cols-2 gap-3">
                                        {Object.entries(stats.skills).map(([key, label]) => (
                                            <div key={key} className="flex items-center gap-2">
                                                <label className="text-sm text-gray-400 w-32">{label}</label>
                                                <input
                                                    type="number"
                                                    value={fixedStats[key] || ''}
                                                    onChange={(e) => updateFixedStat(key, e.target.value ? parseInt(e.target.value) : '')}
                                                    min={1}
                                                    max={100}
                                                    placeholder="0"
                                                    className="w-20 px-2 py-1 bg-slate-700 border border-slate-600 rounded text-white text-sm"
                                                />
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>

                    {/* Crafting Requirements Section */}
                    <div className="bg-slate-800 border border-slate-700 rounded-lg p-6 space-y-4">
                        <h2 className="text-lg font-semibold text-white">Crafting Requirements</h2>
                        <p className="text-sm text-gray-400">
                            Define what is needed to craft this item. Leave empty if the item is GM-awarded only.
                        </p>

                        {/* Building Requirements Guidance */}
                        <div className="p-4 bg-slate-700/50 border border-slate-600 rounded-lg">
                            <div className="flex items-center gap-2 mb-3">
                                <span className="text-purple-400">i</span>
                                <h3 className="text-sm font-medium text-gray-300">Building Requirements by Rarity</h3>
                            </div>
                            <p className="text-xs text-gray-400 mb-3">
                                The building rank determines which tier of building is required to craft items of this rarity.
                            </p>
                            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                                <div className="flex items-center justify-between px-3 py-1.5 bg-slate-800 rounded text-xs">
                                    <span className="text-gray-300 font-medium">Common</span>
                                    <span className="text-gray-400">Rank 1</span>
                                </div>
                                <div className="flex items-center justify-between px-3 py-1.5 bg-slate-800 rounded text-xs">
                                    <span className="text-green-400 font-medium">Uncommon</span>
                                    <span className="text-gray-400">Rank 2</span>
                                </div>
                                <div className="flex items-center justify-between px-3 py-1.5 bg-slate-800 rounded text-xs">
                                    <span className="text-blue-400 font-medium">Rare</span>
                                    <span className="text-gray-400">Rank 3</span>
                                </div>
                                <div className="flex items-center justify-between px-3 py-1.5 bg-slate-800 rounded text-xs">
                                    <span className="text-purple-400 font-medium">Epic</span>
                                    <span className="text-gray-400">Rank 4</span>
                                </div>
                                <div className="flex items-center justify-between px-3 py-1.5 bg-slate-800 rounded text-xs">
                                    <span className="text-orange-400 font-medium">Legendary</span>
                                    <span className="text-gray-400">Rank 5</span>
                                </div>
                            </div>
                        </div>

                        {/* Required Building Field */}
                        <div>
                            <label className="block text-sm font-medium text-gray-300 mb-1">
                                Required Building
                            </label>
                            <select
                                value={requiredBuildingTypeId}
                                onChange={(e) => setRequiredBuildingTypeId(e.target.value ? parseInt(e.target.value) : '')}
                                className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded text-white"
                            >
                                <option value="">No building required</option>
                                {buildingTypes.map(bt => (
                                    <option key={bt.id} value={bt.id}>
                                        {bt.name} (Rank {bt.rank})
                                    </option>
                                ))}
                            </select>
                            {errors.required_building_type_id && <p className="mt-1 text-sm text-red-400">{errors.required_building_type_id}</p>}
                        </div>

                        {/* Building Level and Stars - only show when a building is selected */}
                        {requiredBuildingTypeId && (
                            <div className="grid grid-cols-2 gap-4">
                                {/* Required Building Level */}
                                <div>
                                    <label className="block text-sm font-medium text-gray-300 mb-1">
                                        Required Building Level
                                    </label>
                                    <input
                                        type="number"
                                        value={requiredBuildingLevel}
                                        onChange={(e) => setRequiredBuildingLevel(Math.max(1, Math.min(10, parseInt(e.target.value) || 1)))}
                                        min={1}
                                        max={10}
                                        className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded text-white"
                                    />
                                    <p className="mt-1 text-xs text-gray-500">
                                        Building must be at least this level (1-10)
                                    </p>
                                    {errors.required_building_level && <p className="mt-1 text-sm text-red-400">{errors.required_building_level}</p>}
                                </div>

                                {/* Required Building Stars */}
                                <div>
                                    <label className="block text-sm font-medium text-gray-300 mb-1">
                                        Required Building Stars
                                    </label>
                                    <div className="flex gap-1">
                                        {[1, 2, 3, 4, 5].map((star) => (
                                            <button
                                                key={star}
                                                type="button"
                                                onClick={() => setRequiredBuildingStars(star)}
                                                className={`w-10 h-10 text-xl rounded transition-colors ${
                                                    star <= requiredBuildingStars
                                                        ? 'text-yellow-400 bg-slate-700 hover:bg-slate-600'
                                                        : 'text-gray-600 bg-slate-800 hover:bg-slate-700'
                                                }`}
                                            >
                                                ★
                                            </button>
                                        ))}
                                    </div>
                                    <p className="mt-1 text-xs text-gray-500">
                                        Building must have at least this many stars (1-5)
                                    </p>
                                    {errors.required_building_stars && <p className="mt-1 text-sm text-red-400">{errors.required_building_stars}</p>}
                                </div>
                            </div>
                        )}

                        {/* Learnable By Field */}
                        <div>
                            <label className="block text-sm font-medium text-gray-300 mb-1">
                                Learnable By
                            </label>
                            <select
                                value={learnableBy}
                                onChange={(e) => setLearnableBy(e.target.value as LearnableBy)}
                                className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded text-white"
                            >
                                <option value="no">Not Learnable (GM-awarded only)</option>
                                <option value="blueprint">Blueprint (requires finding and using a blueprint)</option>
                                <option value="building">Building (learned by constructing the required building)</option>
                            </select>
                            <p className="mt-1 text-xs text-gray-500">
                                Determines how players can learn to craft this item.
                            </p>
                            {errors.learnable_by && <p className="mt-1 text-sm text-red-400">{errors.learnable_by}</p>}
                        </div>

                        {/* Material Cost Guidance */}
                        <div className="p-4 bg-slate-700/50 border border-slate-600 rounded-lg">
                            <div className="flex items-center gap-2 mb-3">
                                <span className="text-purple-400">i</span>
                                <h3 className="text-sm font-medium text-gray-300">Material Cost Recommendations (per item)</h3>
                            </div>
                            <div className="overflow-x-auto">
                                <table className="w-full text-xs">
                                    <thead>
                                        <tr className="border-b border-slate-600">
                                            <th className="text-left py-2 pr-3 text-gray-400 font-medium">Rarity</th>
                                            <th className="text-left py-2 px-3 text-gray-400 font-medium">Materials</th>
                                            <th className="text-left py-2 pl-3 text-gray-400 font-medium">Quantity Each</th>
                                        </tr>
                                    </thead>
                                    <tbody className="text-gray-300">
                                        <tr className="border-b border-slate-700/50">
                                            <td className="py-2 pr-3"><span className="text-gray-300">Common</span></td>
                                            <td className="py-2 px-3">2 materials</td>
                                            <td className="py-2 pl-3">30-50 each</td>
                                        </tr>
                                        <tr className="border-b border-slate-700/50">
                                            <td className="py-2 pr-3"><span className="text-green-400">Uncommon</span></td>
                                            <td className="py-2 px-3">2 materials</td>
                                            <td className="py-2 pl-3">50-70 each</td>
                                        </tr>
                                        <tr className="border-b border-slate-700/50">
                                            <td className="py-2 pr-3"><span className="text-blue-400">Rare</span></td>
                                            <td className="py-2 px-3">3 materials</td>
                                            <td className="py-2 pl-3">70-90 each</td>
                                        </tr>
                                        <tr className="border-b border-slate-700/50">
                                            <td className="py-2 pr-3"><span className="text-purple-400">Epic</span></td>
                                            <td className="py-2 px-3">3 materials</td>
                                            <td className="py-2 pl-3">90-120 each</td>
                                        </tr>
                                        <tr>
                                            <td className="py-2 pr-3"><span className="text-orange-400">Legendary</span></td>
                                            <td className="py-2 px-3">4 materials</td>
                                            <td className="py-2 pl-3">120-150 each</td>
                                        </tr>
                                    </tbody>
                                </table>
                            </div>
                        </div>

                        {/* Material Costs */}
                        <div>
                            <div className="flex items-center justify-between mb-2">
                                <label className="block text-sm font-medium text-gray-300">
                                    Material Costs
                                </label>
                                <button
                                    type="button"
                                    onClick={addCraftingMaterial}
                                    disabled={craftingMaterials.length >= MATERIAL_TYPES.length}
                                    className="px-3 py-1 text-sm bg-purple-600 hover:bg-purple-700 text-white rounded transition-colors disabled:opacity-50"
                                >
                                    + Add Material
                                </button>
                            </div>

                            {craftingMaterials.length === 0 ? (
                                <p className="text-gray-400 text-sm">No materials required. Click "Add Material" to add crafting costs.</p>
                            ) : (
                                <div className="space-y-2">
                                    {craftingMaterials.map((material, index) => (
                                        <div key={index} className="flex items-center gap-3 p-3 bg-slate-700 rounded">
                                            <div className="flex-1">
                                                <label className="block text-xs text-gray-400 mb-1">Material Type</label>
                                                <select
                                                    value={material.type}
                                                    onChange={(e) => updateCraftingMaterial(index, 'type', e.target.value)}
                                                    className="w-full px-2 py-1 bg-slate-600 border border-slate-500 rounded text-white text-sm"
                                                >
                                                    {MATERIAL_TYPES.map(m => (
                                                        <option
                                                            key={m.value}
                                                            value={m.value}
                                                            disabled={craftingMaterials.some((cm, i) => i !== index && cm.type === m.value)}
                                                        >
                                                            {m.label}
                                                        </option>
                                                    ))}
                                                </select>
                                            </div>
                                            <div className="w-28">
                                                <label className="block text-xs text-gray-400 mb-1">Quantity</label>
                                                <input
                                                    type="number"
                                                    value={material.quantity}
                                                    onChange={(e) => updateCraftingMaterial(index, 'quantity', parseInt(e.target.value) || 1)}
                                                    min={1}
                                                    className="w-full px-2 py-1 bg-slate-600 border border-slate-500 rounded text-white text-sm"
                                                />
                                            </div>
                                            <button
                                                type="button"
                                                onClick={() => removeCraftingMaterial(index)}
                                                className="mt-5 px-2 py-1 text-red-400 hover:text-red-300 transition-colors"
                                                title="Remove material"
                                            >
                                                x
                                            </button>
                                        </div>
                                    ))}
                                </div>
                            )}
                            {errors.crafting_materials && <p className="mt-1 text-sm text-red-400">{errors.crafting_materials}</p>}
                        </div>
                    </div>

                    {/* Tags */}
                    <div>
                        <label className="block text-sm font-medium text-gray-300 mb-2">
                            Tags <span className="text-gray-500">(auto-managed based on properties)</span>
                        </label>

                        {/* Auto-tag info */}
                        <div className="mb-3 p-3 bg-slate-700/50 border border-slate-600 rounded-lg">
                            <p className="text-xs text-gray-400 mb-2">
                                <span className="text-blue-400">●</span> Auto-tags are added based on rarity, stats, skills, building, and learnable settings.
                                You can still add custom tags manually.
                            </p>
                            <div className="flex gap-2 text-xs">
                                <span className="px-2 py-0.5 bg-blue-900/50 text-blue-300 rounded-full">Auto</span>
                                <span className="px-2 py-0.5 bg-purple-900/50 text-purple-300 rounded-full">Manual</span>
                            </div>
                        </div>

                        {/* Selected Tags */}
                        {selectedTags.length > 0 && (
                            <div className="flex flex-wrap gap-2 mb-3">
                                {selectedTags.map(tag => {
                                    const isAutoTag = autoTags.has(tag);
                                    return (
                                        <span
                                            key={tag}
                                            className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-sm ${
                                                isAutoTag
                                                    ? 'bg-blue-900/50 text-blue-300 border border-blue-700/50'
                                                    : 'bg-purple-900/50 text-purple-300'
                                            }`}
                                            title={isAutoTag ? 'Auto-generated tag (will update with properties)' : 'Manually added tag'}
                                        >
                                            {isAutoTag && <span className="text-blue-400 text-xs">⚡</span>}
                                            {tag}
                                            <button
                                                type="button"
                                                onClick={() => toggleTag(tag)}
                                                className={`ml-1 transition-colors ${
                                                    isAutoTag
                                                        ? 'text-blue-400 hover:text-red-400'
                                                        : 'text-purple-400 hover:text-red-400'
                                                }`}
                                                title={isAutoTag ? 'Remove auto-tag (will re-add if property still set)' : 'Remove tag'}
                                            >
                                                ×
                                            </button>
                                        </span>
                                    );
                                })}
                            </div>
                        )}

                        {/* Available Tags */}
                        {allTags.length > 0 && (
                            <div className="mb-3">
                                <p className="text-xs text-gray-500 mb-2">Click to add existing tags:</p>
                                <div className="flex flex-wrap gap-2">
                                    {allTags
                                        .filter(tag => !selectedTags.includes(tag.name))
                                        .map(tag => (
                                            <button
                                                key={tag.id}
                                                type="button"
                                                onClick={() => toggleTag(tag.name)}
                                                className="px-3 py-1 text-sm bg-slate-700 text-gray-300 rounded-full hover:bg-slate-600 transition-colors"
                                            >
                                                + {tag.name}
                                            </button>
                                        ))}
                                </div>
                            </div>
                        )}

                        {/* New Tag Input */}
                        <div className="flex gap-2">
                            <input
                                type="text"
                                value={newTagInput}
                                onChange={(e) => setNewTagInput(e.target.value)}
                                onKeyDown={handleNewTagKeyDown}
                                placeholder="Create new tag..."
                                className="flex-1 px-3 py-2 bg-slate-700 border border-slate-600 rounded text-white placeholder-gray-400 text-sm"
                                maxLength={50}
                            />
                            <button
                                type="button"
                                onClick={handleAddNewTag}
                                disabled={!newTagInput.trim()}
                                className="px-4 py-2 bg-slate-600 hover:bg-slate-500 text-white rounded text-sm transition-colors disabled:opacity-50"
                            >
                                Add
                            </button>
                        </div>
                        {errors.tags && <p className="mt-1 text-sm text-red-400">{errors.tags}</p>}
                    </div>

                    {/* Submit Button */}
                    <div className="flex gap-4">
                        <button
                            type="submit"
                            disabled={isSubmitting}
                            className="px-6 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded transition-colors disabled:opacity-50"
                        >
                            {isSubmitting ? 'Saving...' : 'Save Changes'}
                        </button>
                        <Link
                            href="/gm/items"
                            className="px-6 py-2 bg-slate-600 hover:bg-slate-500 text-white rounded transition-colors"
                        >
                            Cancel
                        </Link>
                    </div>
                </form>
            </div>
        </GmLayout>
    );
}
